/**
 * IntakeThread
 *
 * The clarifying conversation on the Plan surface. A single column with a
 * progress rail at the top: the point is to feel like a guided intake with a
 * visible end, not an open-ended chat.
 */

import { Component, For, Show } from "solid-js";
import type { ClarifyingQuestion, Message } from "../../api/types";
import { MessageList } from "./MessageList";

export interface IntakeThreadProps {
  messages: Message[];
  /** Compiled questions, for the progress rail */
  questions: ClarifyingQuestion[];
  /** Message awaiting a reply — echoed before the backend has persisted it */
  pending: string | null;
  /** True while the AI is working */
  busy: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  scope: "Scope",
  decision_point: "Decision",
  constraint: "Constraint",
  unknown: "Unknown",
};

export const IntakeThread: Component<IntakeThreadProps> = (props) => {
  const settled = () =>
    props.questions.filter((q) => q.status !== "pending").length;

  const current = () =>
    props.questions.find((q) => q.status === "pending") ?? undefined;

  return (
    <div class="flex flex-col gap-6">
      <Show when={props.questions.length > 0}>
        <div class="flex items-center gap-3">
          <div class="flex gap-1.5" aria-hidden="true">
            <For each={props.questions}>
              {(question) => (
                <span
                  class={
                    "h-1.5 w-6 rounded-full " +
                    (question.status === "pending" ? "bg-line" : "bg-ink")
                  }
                />
              )}
            </For>
          </div>
          <p class="text-xs font-medium text-ink-3">
            <Show
              when={current()}
              fallback={`${props.questions.length} of ${props.questions.length} answered`}
            >
              {(question) => (
                <>
                  Question {settled() + 1} of {props.questions.length}
                  <span class="text-ink-3">
                    {" · "}
                    {CATEGORY_LABELS[question().category] ??
                      question().category}
                  </span>
                </>
              )}
            </Show>
          </p>
        </div>
      </Show>

      <MessageList
        messages={props.messages}
        pending={props.pending}
        busy={props.busy}
      />
    </div>
  );
};

export default IntakeThread;
